import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import ImageUpload from "@/components/ImageUpload";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { PROFILE } from "@/constants/testIds";

const ROLE_LABELS = { admin: "Administración", academy: "Equipo de la academia" };

export default function ProfilePage() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "", phone: user?.phone || "", photo_url: user?.photo_url || null,
  });
  const [passwords, setPasswords] = useState({ current_password: "", new_password: "" });
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const { toast } = useToast();

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/profile", form);
      setUser(data);
      toast({ title: "Perfil actualizado", description: "Tus datos fueron guardados." });
    } catch (err) {
      toast({ title: "Error", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    setChangingPassword(true);
    try {
      await api.post("/profile/change-password", passwords);
      toast({ title: "Contraseña actualizada", description: "Tu contraseña fue cambiada correctamente." });
      setPasswords({ current_password: "", new_password: "" });
    } catch (err) {
      toast({ title: "Error", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="space-y-6 max-w-xl">
      <div>
        <h1 className="font-serif text-3xl sm:text-4xl tracking-tight text-foreground">Mi Perfil</h1>
        <p className="text-base text-muted-foreground mt-1">{ROLE_LABELS[user?.role] || user?.role} &middot; {user?.email}</p>
      </div>

      <div className="bg-white border border-border rounded-xl p-6 sm:p-8 shadow-sm space-y-5">
        <ImageUpload
          currentUrl={form.photo_url}
          onUploaded={(url) => setForm((f) => ({ ...f, photo_url: url }))}
          placeholderUrl={null}
          testId={PROFILE.photoUpload}
        />

        <div>
          <Label className="text-sm text-muted-foreground mb-1 block">Nombre completo</Label>
          <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid={PROFILE.nameInput} />
        </div>

        <div>
          <Label className="text-sm text-muted-foreground mb-1 block">Email</Label>
          <Input value={user?.email} disabled data-testid={PROFILE.emailInput} />
        </div>

        <div>
          <Label className="text-sm text-muted-foreground mb-1 block">Teléfono</Label>
          <Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} data-testid={PROFILE.phoneInput} />
        </div>

        <Button onClick={handleSaveProfile} disabled={saving} className="bg-brand hover:bg-brand-hover text-white" data-testid={PROFILE.saveButton}>
          {saving ? "Guardando..." : "Guardar cambios"}
        </Button>
      </div>

      <div className="bg-white border border-border rounded-xl p-6 sm:p-8 shadow-sm space-y-5">
        <h2 className="font-serif text-xl text-foreground">Cambiar contraseña</h2>
        <div>
          <Label className="text-sm text-muted-foreground mb-1 block">Contraseña actual</Label>
          <Input type="password" value={passwords.current_password} onChange={(e) => setPasswords((p) => ({ ...p, current_password: e.target.value }))} data-testid={PROFILE.currentPasswordInput} />
        </div>
        <div>
          <Label className="text-sm text-muted-foreground mb-1 block">Nueva contraseña</Label>
          <Input type="password" value={passwords.new_password} onChange={(e) => setPasswords((p) => ({ ...p, new_password: e.target.value }))} data-testid={PROFILE.newPasswordInput} />
        </div>
        <Button
          onClick={handleChangePassword}
          disabled={changingPassword || !passwords.current_password || !passwords.new_password}
          variant="outline"
          data-testid={PROFILE.changePasswordButton}
        >
          {changingPassword ? "Actualizando..." : "Cambiar contraseña"}
        </Button>
      </div>
    </div>
  );
}
